import { action, comparer, reaction } from "mobx";
import { GRID_UNIT } from "../Constants";
import { appSettingsMobx } from "../SharedIframeState";
import { ActionType, InputMode, PaneType, PaneViewMode } from "../Types";
import { modifierKey, secondaryModifierKey } from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasAtom,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
  focusedPaneIdAtom,
  inputModeComputed,
  keyboardTransformPaneIdBox,
  sandboxLayoutOverrideBox,
  selectedPaneIdsAtom,
  selectedToolAtom,
  transferPaneInputSourceBox,
} from "./CanvasState";
import { toggleCommandBar } from "./CommandBar";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import {
  KeyboardCommandPriority,
  keydownCodeSet,
  registerKeyboardCommand,
  registerKeyboardCommands,
} from "./Keyboard";
import { toggleMinimap } from "./Minimap";
import { TransformCanvasPositionEventEmitter } from "./PaneComponent";
import { setupFocusPaneKeyboard } from "./PaneKeyboardSelector";
import { paneMutator } from "./PaneMutator";
import {
  addUndoDeletionToStack,
  arePanesPartOfFunctionPane,
  getMinPaneWidth,
  getZoomWithDirection,
  reactionOnCondition,
  resetCanvasOffset,
  setSelectedToolToPointer,
  zoomToLevel,
} from "./SandboxUtils";
import {
  setupZoom,
  ViewportSelectionAlignment,
  zoomToFit,
  zoomToSelection,
} from "./ToolbarZoomButton";
import { showViewportMarksPanelAtom } from "./ViewportMarksPanel";
import { setupExposeZoom } from "./ZoomExpose";

export function setupKeyboardEffects(): () => void {
  const unsubscribes = [
    reactionOnCondition(
      () => keydownCodeSet.has("Space"),
      action(() => {
        if (selectedToolAtom.value[0] !== "pan") {
          selectedToolAtom.value = ["pan", selectedToolAtom.value];
        }
        return () => {
          if (selectedToolAtom.value[0] === "pan") {
            selectedToolAtom.value = selectedToolAtom.value[1] ?? ["pointer"];
          }
        };
      })
    ),
    reactionOnCondition(
      () => selectedToolAtom.value[0] === "pan",
      () =>
        registerKeyboardCommand({
          name: "move canvas viewport",
          keys: [
            "ArrowUp",
            "ArrowDown",
            "ArrowLeft",
            "ArrowRight",
            "Shift-ArrowUp",
            "Shift-ArrowDown",
            "Shift-ArrowLeft",
            "Shift-ArrowRight",
          ],
          modes: [InputMode.Normal, InputMode.Panning],
          allowOtherNonModifierKeys: true,
          allowRepeat: true,
          priority: KeyboardCommandPriority.High,
          handler: action((key) => {
            const distance = GRID_UNIT * (keydownCodeSet.has("Shift") ? 8 : 1);
            switch (key) {
              case "ArrowRight":
              case "Shift-ArrowRight": {
                canvasViewport.offset[0] += distance / canvasViewport.zoom;
                break;
              }
              case "ArrowLeft":
              case "Shift-ArrowLeft": {
                canvasViewport.offset[0] -= distance / canvasViewport.zoom;
                break;
              }
              case "ArrowUp":
              case "Shift-ArrowUp": {
                canvasViewport.offset[1] -= distance / canvasViewport.zoom;
                break;
              }
              case "ArrowDown":
              case "Shift-ArrowDown": {
                canvasViewport.offset[1] += distance / canvasViewport.zoom;
                break;
              }
            }
          }),
        })
    ),
    reaction(
      () => [keydownCodeSet.has("KeyZ"), keydownCodeSet.has("Alt")],
      action(([isZDown, isAltDown]) => {
        if (isZDown) {
          setupZoom(isAltDown ? "out" : "in");
        } else {
          setupZoom(undefined);
        }
      }),
      { equals: comparer.structural }
    ),
    setupFocusPaneKeyboard(),
  ];

  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}

export function setupPaneCanvasKeyboard() {
  const unsubscribes = [
    reactionOnCondition(
      () => selectedToolAtom.value[0] !== "pointer",
      () =>
        registerKeyboardCommand({
          name: "select pointer tool",
          keys: ["v"],
          handler: action(() => {
            selectedToolAtom.value = ["pointer"];
          }),
        })
    ),
    reactionOnCondition(
      () =>
        canCreateNewPaneComputed.get() &&
        !(
          selectedToolAtom.value[0] === "pane" &&
          selectedToolAtom.value[1] === PaneType.Evaluate
        ),
      () =>
        registerKeyboardCommand({
          name: "select eval pane tool",
          keys: ["e"],
          handler: action(() => {
            selectedToolAtom.value = ["pane", PaneType.Evaluate];
          }),
        })
    ),
    reactionOnCondition(
      () =>
        selectedPaneIdsAtom.value.length > 0 ||
        focusedPaneIdAtom.value !== undefined,
      () =>
        registerKeyboardCommands([
          {
            name: "toggle pane collapse",
            keys: ["c"],
            handler() {
              const selectedPaneIds =
                selectedPaneIdsAtom.value.length > 0
                  ? selectedPaneIdsAtom.value
                  : focusedPaneIdAtom.value !== undefined
                  ? [focusedPaneIdAtom.value]
                  : [];
              if (selectedPaneIds.length > 0) {
                const areAllSelectedPanesCollapsed = selectedPaneIds.every(
                  (paneId) =>
                    activePaneStates.get(paneId)?.viewMode.get() ===
                    PaneViewMode.Collapsed
                );
                for (const selectedPaneId of selectedPaneIds) {
                  paneMutator.setCollapsed(
                    selectedPaneId,
                    !areAllSelectedPanesCollapsed
                  );
                }
              }
            },
          },
          {
            name: "delete pane selection",
            keys: ["Backspace"],
            handler() {
              if (selectedPaneIdsAtom.value.length > 0) {
                const functionPaneMessage = arePanesPartOfFunctionPane(
                  selectedPaneIdsAtom.value
                )
                  ? " Doing so will break a connected function pane."
                  : "";
                if (
                  window.confirm(
                    selectedPaneIdsAtom.value.length === 1
                      ? `Delete the selected pane?${functionPaneMessage}`
                      : `Delete the ${selectedPaneIdsAtom.value.length} selected panes?${functionPaneMessage}`
                  )
                ) {
                  addUndoDeletionToStack(selectedPaneIdsAtom.value);
                  canvasDispatch([
                    ActionType.DeletePanes,
                    { paneIds: selectedPaneIdsAtom.value },
                  ]);
                }
              } else if (focusedPaneIdAtom.value !== undefined) {
                if (
                  window.confirm(
                    `Delete the focused pane?${
                      arePanesPartOfFunctionPane([focusedPaneIdAtom.value])
                        ? " Doing do will break a connected function pane."
                        : ""
                    }`
                  )
                ) {
                  addUndoDeletionToStack([focusedPaneIdAtom.value]);
                  canvasDispatch([
                    ActionType.DeletePanes,
                    { paneIds: [focusedPaneIdAtom.value] },
                  ]);
                }
              }
            },
          },
          {
            name: "move viewport (center selection)",
            keys: ["z z", "Shift-@"],
            handler() {
              zoomToSelection(ViewportSelectionAlignment.Center);
            },
          },
          {
            name: "move viewport (top-left selection)",
            keys: ["z t"],
            handler() {
              zoomToSelection(ViewportSelectionAlignment.TopLeft);
            },
          },
          {
            name: "move viewport (bottom-right selection)",
            keys: ["z b"],
            handler() {
              zoomToSelection(ViewportSelectionAlignment.BottomRight);
            },
          },
          {
            name: "transform pane",
            keys: ["t"],
            handler: action(() => {
              const paneId =
                selectedPaneIdsAtom.value.length > 0
                  ? selectedPaneIdsAtom.value[0]
                  : focusedPaneIdAtom.value;
              if (selectedPaneIdsAtom.value.length > 1) {
                selectedPaneIdsAtom.value = [selectedPaneIdsAtom.value[0]];
              }
              if (paneId !== undefined) {
                keyboardTransformPaneIdBox.set(paneId);
              }
            }),
          },
        ])
    ),
    reactionOnCondition(
      () => focusedPaneIdAtom.value !== undefined,
      () =>
        registerKeyboardCommands([
          {
            name: "transform pane",
            keys: [`Ctrl-${secondaryModifierKey}-g`],
            handler: action(() => {
              const focusedPaneId = focusedPaneIdAtom.value!;
              const activePaneState = activePaneStates.get(focusedPaneId);
              const paneOutput = activePaneState!.outputBoxes[0].get();
              if (
                paneOutput[0] !== "value" ||
                typeof paneOutput[1] !== "string"
              ) {
                console.error("value is not a string");
              }
              canvasDispatch([
                ActionType.GenerateLLMText,
                {
                  inputPaneId: focusedPaneId,
                  prompt: paneOutput[1],
                  options: (window as any).llmOptions,
                },
              ]);
            }),
          },
        ])
    ),
    reactionOnCondition(
      () => inputModeComputed.get() === InputMode.TransformPane,
      () =>
        registerKeyboardCommands([
          {
            name: "exit transform pane",
            keys: ["Escape", "Enter"],
            modes: [InputMode.TransformPane],
            handler: action(() => {
              keyboardTransformPaneIdBox.set(undefined);
            }),
          },
          {
            name: "move pane",
            keys: [
              "ArrowUp",
              "ArrowDown",
              "ArrowLeft",
              "ArrowRight",
              "Shift-ArrowUp",
              "Shift-ArrowDown",
              "Shift-ArrowLeft",
              "Shift-ArrowRight",
              "h",
              "j",
              "k",
              "l",
              "Shift-H",
              "Shift-J",
              "Shift-K",
              "Shift-L",
            ],
            modes: [InputMode.TransformPane],
            allowOtherNonModifierKeys: true,
            allowRepeat: true,
            handler: (key) => {
              const paneId = keyboardTransformPaneIdBox.get();
              if (paneId === undefined) {
                return;
              }
              const currentPosition = activePaneStates.get(paneId)?.rect.get();
              if (currentPosition === undefined) {
                return;
              }
              const distance = GRID_UNIT * (key.includes("Shift") ? 8 : 1);
              switch (key) {
                case "l":
                case "Shift-L":
                case "ArrowRight":
                case "Shift-ArrowRight":
                  paneMutator.updatePosition(paneId, [
                    currentPosition[0] + distance,
                    currentPosition[1],
                  ]);
                  break;
                case "h":
                case "Shift-H":
                case "ArrowLeft":
                case "Shift-ArrowLeft":
                  paneMutator.updatePosition(paneId, [
                    currentPosition[0] - distance,
                    currentPosition[1],
                  ]);
                  break;
                case "j":
                case "Shift-J":
                case "ArrowDown":
                case "Shift-ArrowDown":
                  paneMutator.updatePosition(paneId, [
                    currentPosition[0],
                    currentPosition[1] + distance,
                  ]);
                  break;
                case "k":
                case "Shift-K":
                case "ArrowUp":
                case "Shift-ArrowUp":
                  paneMutator.updatePosition(paneId, [
                    currentPosition[0],
                    currentPosition[1] - distance,
                  ]);
                  break;
              }
            },
          },
          {
            name: "resize pane",
            keys: ["[", "]", "Shift-{", "Shift-}"],
            modes: [InputMode.TransformPane],
            allowOtherNonModifierKeys: true,
            allowRepeat: true,
            handler: (key) => {
              const paneId = keyboardTransformPaneIdBox.get();
              if (paneId === undefined) {
                return;
              }
              const activePaneState = activePaneStates.get(paneId);
              if (activePaneState === undefined) {
                return;
              }
              const currentRect = activePaneState.rect.get();
              switch (key) {
                case "[": {
                  const newWidth = currentRect[2] - GRID_UNIT;
                  if (newWidth >= getMinPaneWidth(activePaneState.type)) {
                    paneMutator.updateRect(paneId, [
                      currentRect[0],
                      currentRect[1],
                      newWidth,
                      currentRect[3],
                    ]);
                  }
                  break;
                }
                case "]":
                  paneMutator.updateRect(paneId, [
                    currentRect[0],
                    currentRect[1],
                    currentRect[2] + GRID_UNIT,
                    currentRect[3],
                  ]);
                  break;
                case "Shift-{":
                  paneMutator.updateRect(paneId, [
                    currentRect[0],
                    currentRect[1],
                    currentRect[2],
                    currentRect[3] - GRID_UNIT,
                  ]);
                  break;
                case "Shift-}":
                  paneMutator.updateRect(paneId, [
                    currentRect[0],
                    currentRect[1],
                    currentRect[2],
                    currentRect[3] + GRID_UNIT,
                  ]);
                  break;
              }
            },
          },
        ])
    ),
    reactionOnCondition(
      () => selectedPaneIdsAtom.value.length > 0,
      () =>
        registerKeyboardCommand({
          name: "clear pane selection",
          keys: ["Escape"],
          handler: action(() => {
            selectedPaneIdsAtom.value = [];
          }),
        })
    ),
    reactionOnCondition(
      () => selectedToolAtom.value[0] !== "pointer",
      () =>
        registerKeyboardCommand({
          name: "select pointer tool",
          keys: ["Escape"],
          handler() {
            setSelectedToolToPointer();
            return false;
          },
        })
    ),
    reactionOnCondition(
      () => transferPaneInputSourceBox.get() !== undefined,
      () =>
        registerKeyboardCommand({
          name: "cancel transfer outputs",
          keys: ["Escape"],
          handler: action(() => {
            transferPaneInputSourceBox.set(undefined);
          }),
        })
    ),
    registerKeyboardCommand({
      name: "toggle minimap",
      keys: ["m"],
      handler() {
        toggleMinimap();
      },
    }),
    registerKeyboardCommands([
      {
        name: "zoom out",
        keys: ["-", "Shift-_"],
        modes: [InputMode.Normal, InputMode.Panning],
        allowOtherNonModifierKeys: true,
        handler() {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "out");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        },
      },
      {
        name: "zoom in",
        keys: ["=", "Shift-+"],
        modes: [InputMode.Normal, InputMode.Panning],
        allowOtherNonModifierKeys: true,
        handler() {
          const nextZoom = getZoomWithDirection(canvasViewport.zoom, "in");
          if (nextZoom !== undefined) {
            zoomToLevel(nextZoom);
          }
        },
      },
      {
        name: "zoom to fit",
        keys: ["Shift-!"],
        modes: [InputMode.Normal, InputMode.Panning],
        allowOtherNonModifierKeys: true,
        handler() {
          zoomToFit();
        },
      },
      {
        name: "reset viewport",
        keys: ["0"],
        modes: [InputMode.Normal, InputMode.Panning],
        allowOtherNonModifierKeys: true,
        handler: action(() => {
          canvasViewportShouldAnimateAtom.value = true;
          resetCanvasOffset();
          canvasViewport.zoom = 1;
        }),
      },
      {
        name: "reset zoom",
        keys: ["Shift-)"],
        modes: [InputMode.Normal, InputMode.Panning],
        allowOtherNonModifierKeys: true,
        handler: action(() => {
          canvasViewportShouldAnimateAtom.value = true;
          zoomToLevel(1);
        }),
      },
      {
        name: "orient visible panes",
        keys: ["Shift-O"],
        handler() {
          if (
            window.confirm("Do you want to orient visible panes at the origin?")
          ) {
            const filteredPaneStates = [...activePaneStates.entries()].filter(
              ([, activePaneState]) =>
                activePaneState.viewMode.get() !== PaneViewMode.Hidden
            );
            const paneIds = filteredPaneStates.map(([paneId]) => paneId);
            const rectsComputed = filteredPaneStates.map(
              ([, activePaneState]) => activePaneState.rect
            );
            let [minX, minY] = rectsComputed[0].get();
            for (let i = 1; i < rectsComputed.length; i++) {
              minX = Math.min(minX, rectsComputed[i].get()[0]);
              minY = Math.min(minY, rectsComputed[i].get()[1]);
            }
            const offsetX = -minX + 32;
            const offsetY = -minY + 32;
            TransformCanvasPositionEventEmitter.emit("transform", {
              paneIds,
              offset: [offsetX, offsetY],
            });
            resetCanvasOffset();
          }
        },
      },
    ]),
    registerKeyboardCommand({
      name: "save canvas to file",
      keys: ["Meta-s"],
      handler(_key, e) {
        async function go() {
          const canvas = canvasAtom.value;
          await ParentComlinkAPI.saveCanvasToFile(canvas, "keyboard");
        }
        go();
        e?.preventDefault();
      },
    }),
    registerKeyboardCommand({
      name: "toggle marks panel",
      keys: [`Ctrl-${secondaryModifierKey}-m`],
      handler: action(() => {
        showViewportMarksPanelAtom.value = !showViewportMarksPanelAtom.value;
      }),
    }),
    registerKeyboardCommand({
      name: "toggle layout sandbox",
      keys: [`Ctrl-${secondaryModifierKey}-l`],
      handler: action(() => {
        if (sandboxLayoutOverrideBox.get() !== undefined) {
          sandboxLayoutOverrideBox.set(undefined);
        } else {
          sandboxLayoutOverrideBox.set({});
        }
      }),
    }),
    registerKeyboardCommand({
      name: "toggle dark mode",
      keys: [`Shift-${modifierKey}-d`],
      handler: action((_, e) => {
        appSettingsMobx.theme =
          appSettingsMobx.theme === "dark" ? "light" : "dark";
        e?.preventDefault();
      }),
    }),
    registerKeyboardCommand({
      name: "toggle command bar",
      keys: [`${modifierKey}-k`, `${modifierKey}-p`],
      handler: action((_, e) => {
        toggleCommandBar();
        e?.preventDefault();
      }),
    }),
    registerKeyboardCommand({
      name: "toggle vim mode",
      keys: ["Ctrl-Meta-v"],
      handler: action(() => {
        appSettingsMobx.vimEnabled = !appSettingsMobx.vimEnabled;
      }),
    }),
    ...setupExposeZoom(),
  ];

  return () => {
    unsubscribes.forEach((unsubscribe) => unsubscribe());
  };
}
